/* global globalThis: readonly */
export const CHECK_TTL_MS = 5 * 60 * 1000;

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);

export const parseTranslationPayload = raw => {
    try {
        const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!isObject(value) || !isObject(value.languages) || !isObject(value.translations)) return null;
        const languages = Object.fromEntries(Object.entries(value.languages).filter(([, label]) => typeof label === 'string'));
        const translations = Object.fromEntries(Object.entries(value.translations).filter(([, messages]) => isObject(messages)));
        if (!Object.keys(languages).length || !Object.keys(translations).length) return null;
        return { timestamp: typeof value.timestamp === 'string' ? value.timestamp : null, languages, translations };
    } catch {
        return null;
    }
};

export const getTranslationCacheKey = async token => {
    if (!token) return null;
    try {
        // Hashing is one-way; never persist the delegated token or use it as a fallback key.
        const digest = await globalThis.crypto.subtle.digest(
            'SHA-256', new TextEncoder().encode(`selection.translation.v1:${token}`),
        );
        return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('').slice(0, 32);
    } catch {
        return null;
    }
};

export const resolveLanguage = (requested, payload) => {
    const normalized = typeof requested === 'string' ? requested.trim().toLowerCase().replace(/_/g, '-') : '';
    const available = Object.keys(payload?.translations || {});
    return available.find(lang => lang.toLowerCase() === normalized)
        || available.find(lang => lang.toLowerCase() === normalized.split('-')[0])
        || 'en';
};

// Dependencies are injected so cache, synchronization and race handling can be tested independently.
export const createTranslationService = ({ engine, getJson, getLatest, getKey = getTranslationCacheKey,
    getStorage = () => window.localStorage, now = Date.now }) => {
    let active = null;
    const replaceResources = (payload, lang) => {
        Object.keys(engine.store?.data || {}).forEach(language => engine.removeResourceBundle(language, 'translation'));
        Object.entries(payload?.translations || {}).forEach(([language, messages]) => {
            engine.addResourceBundle(language, 'translation', messages, true, true);
        });
        return engine.changeLanguage(resolveLanguage(lang, payload));
    };
    const persist = scope => {
        if (!scope.key || active !== scope) return;
        try {
            getStorage().setItem(scope.key, JSON.stringify({
                version: 1, client: scope.client, payload: scope.payload, checkedAt: scope.checkedAt,
            }));
        } catch { /* Storage restrictions must not prevent in-memory translations. */ }
    };
    const refresh = () => {
        const scope = active;
        if (!scope?.token) return Promise.resolve(false);
        if (scope.pending) return scope.pending;
        scope.pending = (async () => {
            try {
                await scope.ready;
                if (active !== scope) return false;
                if (scope.payload && scope.checkedAt && now() - scope.checkedAt < CHECK_TTL_MS) return true;
                let fetchJson = !scope.payload;
                if (!fetchJson) {
                    const latest = await getLatest({ token: scope.token });
                    if (!latest || typeof latest.timestamp !== 'string') throw new Error('Invalid translation timestamp');
                    fetchJson = !scope.payload.timestamp || latest.timestamp !== scope.payload.timestamp;
                }
                if (fetchJson) {
                    const payload = parseTranslationPayload(await getJson({ token: scope.token, client: scope.client }));
                    if (!payload) throw new Error('Invalid translation payload');
                    if (active !== scope) return false;
                    scope.payload = payload;
                    await replaceResources(payload, scope.lang);
                }
                if (active !== scope) return false;
                scope.checkedAt = now();
                persist(scope);
                return true;
            } catch {
                // Keep the last valid payload during backend failures; never log tokens or request errors.
                return false;
            }
        })().finally(() => { scope.pending = null; });
        return scope.pending;
    };
    const start = ({ token, client = null, lang }) => {
        if (active && active.token === token && active.client === client) {
            active.lang = lang;
            if (active.payload) replaceResources(active.payload, lang);
            return refresh();
        }
        const scope = { token, client, lang, payload: null, checkedAt: 0, key: null };
        active = scope;
        replaceResources(null, lang);
        scope.ready = (async () => {
            scope.key = await getKey(token);
            if (active !== scope || !scope.key) return;
            try {
                const cached = JSON.parse(getStorage().getItem(scope.key));
                if (cached?.version !== 1 || cached.client !== client) return;
                const payload = parseTranslationPayload(cached.payload);
                if (!payload) return;
                scope.payload = payload;
                scope.checkedAt = Number.isFinite(cached.checkedAt) && cached.checkedAt <= now() ? cached.checkedAt : 0;
                await replaceResources(payload, scope.lang);
            } catch { /* Invalid or inaccessible cache is treated as a cache miss. */ }
        })();
        return refresh();
    };
    return { start, refresh };
};
